import React, { FC } from 'react';
import { Navigate, useLocation, useMatch } from 'react-router-dom';

import { Capability, Layouts } from 'components';
import { useCapabilities } from 'hooks';
import * as paths from 'paths';

import AdminNav from './components/AdminNav';
import * as Containers from './containers';

enum AdminLayout {
  OneColumn,
  TwoColumn,
}

const AdminController: FC = () => {
  const { pathname } = useLocation();
  const { isAdmin, isModerator } = useCapabilities();
  const isAssignmentsEdit = !!useMatch(paths.adminSettingsAssignmentsEdit);
  const isAdminContentEmailEdit = !!useMatch(paths.adminContentEmailEdit);
  const isAdminLeaderboardEdit = !!useMatch(paths.adminLeaderboardsEdit);

  if (!isAdmin && !isModerator) {
    return <Navigate to={paths.index} replace />;
  }

  function renderAdminRoutes() {
    let main = null;
    let layout = AdminLayout.TwoColumn;

    switch (pathname) {
      case paths.adminAnalytics:
        main = <Navigate to={paths.adminAnalyticsOverview} replace />;
        break;
      case paths.adminAnalyticsOverview:
        main = (
          <Capability name="Admin.Analytics.Overview.View.Access">
            <Containers.AnalyticsOverview />
          </Capability>
        );
        break;
      case paths.adminAnalyticsGroups:
        main = (
          <Capability name="Admin.Analytics.Groups.View.Access">
            <Containers.AnalyticsGroups />
          </Capability>
        );
        break;
      case paths.adminAnalyticsUsers:
        main = (
          <Capability name="Admin.Analytics.Users.View.Access">
            <Containers.AnalyticsUsers />
          </Capability>
        );
        break;
      case paths.adminAnalyticsPosts:
        main = (
          <Capability name="Admin.Analytics.Posts.View.Access">
            <Containers.AnalyticsPosts />
          </Capability>
        );
        break;
      case paths.adminAnalyticsShares:
        main = (
          <Capability name="Admin.Analytics.Shares.View.Access">
            <Containers.AnalyticsShares />
          </Capability>
        );
        break;
      case paths.adminAnalyticsLeaderboards:
        main = (
          <Capability name="Admin.Analytics.Leaderboard.View.Access">
            <Containers.AnalyticsLeaderboards />
          </Capability>
        );
        break;
      case paths.adminPostManagement:
        main = (
          <Capability name="Admin.PostManagement.View.Access">
            <Containers.PostManagement />
          </Capability>
        );
        break;
      case paths.adminUserManagement:
        main = (
          <Capability name="Admin.UserManagement.View.Access">
            <Containers.UserManagement />
          </Capability>
        );
        break;
      case paths.adminInvite:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.People.Invite.View.Access">
            <Containers.PeopleInvite />
          </Capability>
        );
        break;
      case paths.adminGroups:
        main = (
          <Capability name="Admin.Groups.View.Access">
            <Containers.Groups />
          </Capability>
        );
        break;
      case paths.adminInvitations:
        main = (
          <Capability name="Admin.Invitations.View.Access">
            <Containers.Invitations />
          </Capability>
        );
        break;
      case paths.adminMessages:
        main = (
          <Capability name="Admin.Messages.View.Access">
            <Containers.Messages />
          </Capability>
        );
        break;
      case paths.adminLeaderboards:
        main = (
          <Capability name="Admin.Leaderboards.View.Access">
            <Containers.Leaderboards />
          </Capability>
        );
        break;
      case paths.adminLeaderboardsCreate:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Leaderboards.View.Access">
            <Containers.LeaderboardsCreate />
          </Capability>
        );
        break;
      case paths.adminContentEmails:
        main = (
          <Capability name="Admin.ContentEmails.View.Access">
            <Containers.ContentEmailsLoader />
          </Capability>
        );
        break;
      case paths.adminSettings:
      case paths.adminSettingsLocations:
      case paths.adminSettingsDisclosures:
      case paths.adminSettingsPolicies:
      case paths.adminSettingsReferrals:
      case paths.adminSettingsIntegrations:
      case paths.adminSettingsCompanyPages:
        main = (
          <Capability name="Admin.Settings.Account.View.Access">
            <Containers.Account />
          </Capability>
        );
        break;
      case paths.adminSettingsAccount:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Settings.Account.View.Access">
            <Containers.AccountSettings />
          </Capability>
        );
        break;
      case paths.adminSettingsAssignments:
        main = (
          <Capability name="Admin.Settings.Assignments.View.Access">
            <Containers.Assignments />
          </Capability>
        );
        break;
      case paths.adminSettingsAssignmentsCreate:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Settings.Assignments.View.Access">
            <Containers.AssignmentCreate />
          </Capability>
        );
        break;
      case paths.adminSettingsUserSegments:
        main = (
          <Capability name="Admin.Settings.Segments.View.Access">
            <Containers.Segments />
          </Capability>
        );
        break;
      case paths.adminSettingsShareTracking:
        main = (
          <Capability name="Admin.Settings.ShareUrlTracking.View.Access">
            <Containers.Parameters />
          </Capability>
        );
        break;
      case paths.adminSettingsBlocklist:
      case paths.adminSettingsSharing:
      case paths.adminSettingsStickers:
        main = (
          <Capability name="Admin.Settings.Sharing.View.Access">
            <Containers.Sharing />
          </Capability>
        );
        break;
      case paths.adminUpgrade:
        main = (
          <Capability name="Access.Owner">
            <Containers.Upgrade />
          </Capability>
        );
        break;
      case paths.adminBilling:
        main = (
          <Capability name="Access.Owner">
            <Containers.Billing />
          </Capability>
        );
        break;
      case paths.adminContentEmailCreate:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.ContentEmails.View.Access">
            <Containers.ContentEmailsLoader />
          </Capability>
        );
        break;
    }

    if (!main) {
      if (isAssignmentsEdit) {
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Settings.Assignments.View.Access">
            <Containers.AssignmentLoader />
          </Capability>
        );
      } else if (isAdminContentEmailEdit) {
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.ContentEmails.View.Access">
            <Containers.ContentEmailsLoader />
          </Capability>
        );
      } else if (isAdminLeaderboardEdit) {
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Leaderboards.View.Access">
            <Containers.LeaderboardConfigLoader />
          </Capability>
        );
      }
    }

    if (!main) {
      throw new Error('Unhandled Admin Route');
    }

    if (layout === AdminLayout.OneColumn) {
      return <Layouts.App1Column main={main} />;
    }

    return <Layouts.App2Column desktopSplit="2/10" sidebar={<AdminNav />} main={main} />;
  }

  function renderGroupModeratorRoutes() {
    let main = null;

    switch (pathname) {
      case paths.adminAnalytics:
        main = <Navigate to={paths.adminAnalyticsGroups} replace />;
        break;
      case paths.adminAnalyticsGroups:
        main = (
          <Capability name="Admin.Analytics.Groups.View.Access">
            <Containers.AnalyticsGroups />
          </Capability>
        );
        break;
      case paths.adminAnalyticsPosts:
        main = (
          <Capability name="Admin.Analytics.Posts.View.Access">
            <Containers.AnalyticsPosts />
          </Capability>
        );
        break;
      case paths.adminAnalyticsShares:
        main = (
          <Capability name="Admin.Analytics.Shares.View.Access">
            <Containers.AnalyticsShares />
          </Capability>
        );
        break;
      case paths.adminAnalyticsUsers:
        main = (
          <Capability name="Admin.Analytics.Users.View.Access">
            <Containers.AnalyticsUsers />
          </Capability>
        );
        break;
      case paths.adminPostManagement:
        main = (
          <Capability name="Admin.PostManagement.View.Access">
            <Containers.PostManagement />
          </Capability>
        );
        break;
      default:
        throw new Error('Unhandled Group Moderator Route');
    }

    return <Layouts.App2Column desktopSplit="2/10" sidebar={<AdminNav />} main={main} />;
  }

  return isAdmin ? renderAdminRoutes() : renderGroupModeratorRoutes();
};

export default AdminController;
